/**
 * Media helpers — canvas capture, recorder setup, and the base64 conversions the
 * /jud/finalize body needs.
 */

import {
  VIDEO_BITS_PER_SECOND,
  AUDIO_BITS_PER_SECOND,
  VIDEO_CAPTURE_WIDTH,
  VIDEO_CAPTURE_HEIGHT,
  VIDEO_CAPTURE_FPS,
} from "@/constants";

/**
 * getUserMedia constraints for the recording step.
 *
 * Every value is `ideal`, never `exact`: a phone that cannot hit 640x480 at 24 fps should
 * hand back the nearest thing it has, not fail the request and leave the step with no
 * camera. The size is a hint the encoder respects on every browser worth counting, and on
 * one that ignores it the bitrate cap still holds the payload down.
 *
 * A portrait phone typically reports these transposed (480x640) — that is the same pixel
 * budget and the same answer, so it is not worth fighting over.
 */
export const RECORDING_VIDEO_CONSTRAINTS = {
  width: { ideal: VIDEO_CAPTURE_WIDTH },
  height: { ideal: VIDEO_CAPTURE_HEIGHT },
  frameRate: { ideal: VIDEO_CAPTURE_FPS, max: VIDEO_CAPTURE_FPS },
};

/** Mono, cleaned up: one speaker close to a phone mic, read back by a verifier. */
export const RECORDING_AUDIO_CONSTRAINTS = {
  channelCount: { ideal: 1 },
  echoCancellation: true,
  noiseSuppression: true,
};

/**
 * Container preference for the challenge clip, most-preferred first.
 *
 * The legacy app recorded through RecordRTC with `mimeType: "video/webm"` pinned, so every
 * clip the operator panel has ever been handed was WebM, and the panel was built against
 * that. Letting the browser choose instead is invisible on Chrome — it picks WebM anyway —
 * and a silent format change on iOS Safari, whose only MediaRecorder output is MP4/H.264.
 * Those were the clips that stopped playing in the panel. So the pin is back.
 *
 * VP8 specifically, not VP9: VP8 is what the panel has been decoding for years, and a
 * verification backlog is not the place to discover whether its player handles VP9.
 *
 * iOS Safari supports none of these — `isTypeSupported` is false for every WebM type and
 * there is no way to make it emit WebM — so those clips are still MP4. Nothing here can fix
 * that; the panel has to accept both. Note that `stripDataUri` drops the container on the
 * way into /jud/finalize, so the payload does not say which one it is.
 */
const MIME_PREFERENCE = ["video/webm;codecs=vp8,opus", "video/webm;codecs=vp8", "video/webm"];

/**
 * The best-supported WebM flavour, or "" to let the browser pick (which on iOS means MP4).
 */
export function pickRecorderMimeType() {
  if (typeof MediaRecorder === "undefined" || !MediaRecorder.isTypeSupported) return "";
  return MIME_PREFERENCE.find((type) => MediaRecorder.isTypeSupported(type)) || "";
}

/**
 * Build the MediaRecorder for the challenge clip.
 *
 * Pins the container (above) and caps the bitrate (see VIDEO_BITS_PER_SECOND) — but a
 * recorder is also the one thing this step cannot do without, so an options object some
 * browser dislikes must not be what stops someone recording. Anything thrown here falls
 * back to the unconfigured recorder: a clip in an awkward container still beats no clip,
 * and the user has already read the phrase out loud by the time it would fail.
 */
export function createRecorder(stream) {
  const options = {
    videoBitsPerSecond: VIDEO_BITS_PER_SECOND,
    audioBitsPerSecond: AUDIO_BITS_PER_SECOND,
  };
  const mimeType = pickRecorderMimeType();
  if (mimeType) options.mimeType = mimeType;

  try {
    return new MediaRecorder(stream, options);
  } catch (e) {
    return new MediaRecorder(stream);
  }
}

/**
 * Grab a still frame from a live <video>, mirrored to match the preview.
 *
 * The preview is CSS-mirrored (`transform: scaleX(-1)`) because an un-mirrored selfie
 * view feels wrong to use. The canvas has to apply the same flip, or the saved photo
 * comes out reversed relative to what the user lined up.
 *
 * @returns {string|null} a `data:image/jpeg;base64,…` URL, or null if no frame is ready yet.
 */
export function captureFrame(video) {
  if (!video || !video.videoWidth) return null;
  const canvas = document.createElement("canvas");
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  const ctx = canvas.getContext("2d");
  ctx.translate(canvas.width, 0);
  ctx.scale(-1, 1);
  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL("image/jpeg", 0.9);
}

/** Read a Blob as a full `data:<mime>;base64,<payload>` URL. */
export function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

/**
 * Drop the `data:…;base64,` prefix.
 *
 * The backend wants the raw payload; the same value is reused un-stripped as an <img>/<video>
 * src for the review screen, so the strip happens at the request boundary, not at capture.
 *
 * Split on the `;base64,` marker, not on the first comma. A media type can carry a parameter
 * that contains a comma, and exactly one does here: a Chrome recording blob is typed
 * `video/webm;codecs=vp8,opus`, so its data URL reads
 *
 *   data:video/webm;codecs=vp8,opus;base64,GkXfo59ChoEB…
 *
 * and cutting at the first comma shipped `opus;base64,GkXfo59ChoEB…` as the payload. The
 * backend's base64 decoder discarded the two characters it could not read and kept the ten
 * it could, so every stored clip was the real stream with `opusbase64` prepended and every
 * byte after it shifted out of alignment — a file nothing could play. Images were unaffected
 * (`data:image/jpeg;base64,…` has no parameter), which is why only the video broke, and only
 * on browsers that report the codec list. iOS Safari types its blobs `video/mp4`, with no
 * comma, and those clips were fine.
 */
export function stripDataUri(dataUrl) {
  if (!dataUrl) return "";
  const marker = ";base64,";
  const at = dataUrl.indexOf(marker);
  if (at !== -1) return dataUrl.slice(at + marker.length);
  // Not a base64 data URL: a bare payload passes through, anything else loses its header.
  const comma = dataUrl.indexOf(",");
  return comma === -1 ? dataUrl : dataUrl.slice(comma + 1);
}
